import { Injectable, NgZone } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Subject, fromEvent } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { EventRegistrationInput } from "../../models/event-registration-input";
import { CesiumMapService } from "../cesium-map.service";
import { MapActionType, MapEntitiesEditorService } from "../entity-editors/map-entities-editor.service";
import { DisposableAndPausableObservable } from "./cesium-events.model";
import { DtmMapEventsManagerService, EventResult } from "./map-events-manager.service";

export enum CesiumPointerType {
    None = "unset",
    HeightHandle = "n-resize",
    Move = "url(/assets/cursors/drag-move.svg) 9.5 9.5, move",
    Select = "url(/assets/cursors/hand.svg) 0 7, pointer",
    DrawCylinder = "url(/assets/cursors/pen-nib-oval.svg), auto",
    DrawTakeoffRunway = "url(/assets/cursors/pen-nib-takeoff.svg), auto",
    DrawLandingRunway = "url(/assets/cursors/pen-nib-land.svg), auto",
    DrawPolyline = "url(/assets/cursors/pen-nib-line.svg), auto",
    DrawPrism = "url(/assets/cursors/pen-nib-prism.svg), auto",
    Info = "help",
    Pointer = "pointer",
}

const DRILL_PICK_LIMIT = 100; // NOTE: this is needed to prevent infinite loop due to Cesium bug when drillpicking outside of viewport
const DRILL_PICK_BUFFER_SIZE = 5;
const WHOLE_MAP_ID = "MAP";
const EVENT_PRIORITY = 100;

export enum ViewerContainerEventType {
    MouseOut = "mouseout",
}

@UntilDestroy()
@Injectable()
export class CesiumPointerManagerService {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private cesiumViewer: any;
    private pointerType = new Map<string, CesiumPointerType>();

    constructor(
        private readonly cesiumService: CesiumMapService,
        private readonly mapEventsManager: DtmMapEventsManagerService,
        private readonly mapEntitiesEditorService: MapEntitiesEditorService,
        private readonly ngZone: NgZone
    ) {
        // NOTE:  Initialize viewer and subscribe to external events outside Angular to avoid unnecessary change detection
        this.ngZone.runOutsideAngular(() => {
            this.cesiumViewer = cesiumService.getViewer();
            this.watchForMapActionsChange();
        });
    }

    private watchForMapActionsChange() {
        // NOTE: Do not extract this const outside of this class as it will cause unreasonable errors :)
        // TODO: check if this is still needed in angular 16 with new TypeScript version DTM-3352
        const mapActionToCesiumPointerMap = new Map<MapActionType, CesiumPointerType>([
            [MapActionType.DrawCylinder, CesiumPointerType.DrawCylinder],
            [MapActionType.DrawTakeoffRunway, CesiumPointerType.DrawTakeoffRunway],
            [MapActionType.DrawLandingRunway, CesiumPointerType.DrawLandingRunway],
            [MapActionType.DrawPolyline, CesiumPointerType.DrawPolyline],
            [MapActionType.DrawPolylineCorridor, CesiumPointerType.DrawPolyline],
            [MapActionType.DrawPrism, CesiumPointerType.DrawPrism],
            [MapActionType.DrawAssistedLandingRunway, CesiumPointerType.DrawLandingRunway],
            [MapActionType.DrawAssistedTakeoffRunway, CesiumPointerType.DrawTakeoffRunway],
        ]);

        // NOTE: subscribe outside Angular zone to prevent change detection triggering from map action events
        this.ngZone.runOutsideAngular(() => {
            this.mapEntitiesEditorService.activeMapAction$.pipe(untilDestroyed(this)).subscribe((action) => {
                const pointerType = mapActionToCesiumPointerMap.get(action);

                this.setPointerType(WHOLE_MAP_ID, pointerType ?? CesiumPointerType.None);
            });
        });
    }

    public setPointerType(entityId: string, pointerType: CesiumPointerType) {
        this.pointerType.set(entityId, pointerType);
        this.refreshCursor();
    }

    public removePointer(entityId: string) {
        this.pointerType.delete(entityId);
        this.refreshCursor();
    }

    private refreshCursor() {
        for (const pointer of this.pointerType.values()) {
            if (pointer !== CesiumPointerType.None) {
                this.cesiumViewer.container.style.cursor = pointer;

                return;
            }
        }

        this.cesiumViewer.container.style.cursor = CesiumPointerType.None;
    }

    public addViewerContainerEventHandler(input: ViewerContainerEventType): DisposableAndPausableObservable<void> {
        const disposableSubject = new Subject<void>();

        // NOTE:  create DOM event observable outside Angular to avoid triggering change detection on every DOM event
        const observable = this.ngZone.runOutsideAngular(() =>
            fromEvent(this.cesiumViewer.container, input).pipe(
                map(() => undefined),
                takeUntil(disposableSubject)
            )
        ) as DisposableAndPausableObservable<void>;

        observable.dispose = () => {
            disposableSubject.next();
            disposableSubject.complete();
        };

        return observable;
    }
    /**
     * This method wraps mapEventsManager.register method to ensure optimal usage of map features picking
     */
    public addEventHandler(input: EventRegistrationInput): DisposableAndPausableObservable<EventResult> {
        return this.mapEventsManager.register({
            ...input,
            pickConfig: {
                pickHeight: DRILL_PICK_BUFFER_SIZE,
                pickWidth: DRILL_PICK_BUFFER_SIZE,
                drillPickLimit: DRILL_PICK_LIMIT,
            },
            priority: EVENT_PRIORITY,
        });
    }
}
